import { memo, useRef, useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';

const MAX_EVENTS = 100;

const EVENT_CONFIG = {
  VIOLATION_DETECTED: {
    label: 'Violation',
    color: 'bg-red-100 text-red-800 border-red-200',
    dotColor: 'bg-red-500',
  },
  VIOLATION_CLEARED: {
    label: 'Cleared',
    color: 'bg-green-100 text-green-800 border-green-200',
    dotColor: 'bg-green-500',
  },
  PERSON_ENTERED: {
    label: 'Entered',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    dotColor: 'bg-blue-500',
  },
  PERSON_LEFT: {
    label: 'Left',
    color: 'bg-slate-100 text-slate-600 border-slate-200',
    dotColor: 'bg-slate-400',
  },
};

function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function diffDetections(prev, curr) {
  const events = [];
  const now = new Date();
  const prevMap = new Map((prev?.persons || []).map((p) => [p.trackId, p]));
  const currMap = new Map((curr?.persons || []).map((p) => [p.trackId, p]));

  // Check for new persons and new violations
  for (const [trackId, person] of currMap) {
    const prevPerson = prevMap.get(trackId);
    if (!prevPerson) {
      events.push({
        id: `${Date.now()}-enter-${trackId}`,
        type: 'PERSON_ENTERED',
        timestamp: now,
        description: `Track #${trackId} entered${person.zone ? ` (${person.zone})` : ''}`,
      });
    }

    // Check for new violations
    const prevViolations = new Set(prevPerson?.violations || []);
    for (const v of person.violations) {
      if (!prevViolations.has(v)) {
        events.push({
          id: `${Date.now()}-viol-${trackId}-${v}`,
          type: 'VIOLATION_DETECTED',
          timestamp: now,
          description: `Track #${trackId}: ${v.replace(/_/g, ' ')}`,
        });
      }
    }

    // Check for cleared violations
    if (prevPerson) {
      const currViolations = new Set(person.violations);
      for (const v of prevPerson.violations) {
        if (!currViolations.has(v)) {
          events.push({
            id: `${Date.now()}-clear-${trackId}-${v}`,
            type: 'VIOLATION_CLEARED',
            timestamp: now,
            description: `Track #${trackId}: ${v.replace(/_/g, ' ')} cleared`,
          });
        }
      }
    }
  }

  // Check for persons who left
  for (const [trackId, person] of prevMap) {
    if (!currMap.has(trackId)) {
      events.push({
        id: `${Date.now()}-left-${trackId}`,
        type: 'PERSON_LEFT',
        timestamp: now,
        description: `Track #${trackId} left${person.zone ? ` (${person.zone})` : ''}`,
      });
    }
  }

  return events;
}

const EventEntry = memo(({ event }) => {
  const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.PERSON_ENTERED;
  return (
    <div className="flex items-start gap-2 py-1.5 px-2 hover:bg-slate-50 rounded transition-colors">
      <span className={clsx('w-2 h-2 rounded-full mt-1.5 flex-shrink-0', config.dotColor)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={clsx('inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border', config.color)}>
            {config.label}
          </span>
          <span className="text-[10px] text-slate-400 font-mono">{formatTime(event.timestamp)}</span>
        </div>
        <p className="text-xs text-slate-600 mt-0.5 truncate">{event.description}</p>
      </div>
    </div>
  );
});

EventEntry.displayName = 'EventEntry';

const DetectionEventFeed = ({ detections, className }) => {
  const [events, setEvents] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef(null);
  const prevDetectionsRef = useRef(null);
  const isHoveringRef = useRef(false);

  // Diff consecutive detection payloads to generate events
  useEffect(() => {
    if (!detections) return;

    const prev = prevDetectionsRef.current;
    prevDetectionsRef.current = detections;

    // Skip first frame (no previous to diff against)
    if (!prev) return;

    const newEvents = diffDetections(prev, detections);
    if (newEvents.length === 0) return;

    setEvents((current) => {
      const updated = [...current, ...newEvents];
      // FIFO overflow
      if (updated.length > MAX_EVENTS) {
        return updated.slice(updated.length - MAX_EVENTS);
      }
      return updated;
    });
  }, [detections]);

  // Auto-scroll to bottom unless user is hovering (paused)
  useEffect(() => {
    if (!isHoveringRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events]);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    setIsPaused(false);
    // Scroll to bottom when unpaused
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  const handleClear = useCallback(() => {
    setEvents([]);
  }, []);

  return (
    <div className={clsx('flex flex-col bg-white border-t border-slate-200', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Event Feed</h4>
          <span className="text-[10px] text-slate-400">{events.length} events</span>
          {isPaused && (
            <span className="text-[10px] text-amber-600 font-medium">PAUSED</span>
          )}
        </div>
        <button
          onClick={handleClear}
          className="text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Events */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {events.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No events yet</p>
        ) : (
          events.map((event) => <EventEntry key={event.id} event={event} />)
        )}
      </div>
    </div>
  );
};

export default memo(DetectionEventFeed);
